import {
  Alert,
  Box,
  Button,
  Container,
  DatePicker,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Spinner,
  Table,
  TableProps,
  TimeInput
} from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FormikProps } from 'formik';
import {
  FinanceCalendarEventEntity,
  FINANCE_CALENDAR_SCENARIOS,
  toFinanceCalendarWidgetData
} from '../../../widgets/finance-calendar-widget/FinanceCalendarData';
import { FinanceCalendarWidgetContent } from '../../../widgets/finance-calendar-widget/FinanceCalendarWidget';
import { fetchFinanceCalendarEvents, putFinanceCalendarEventsMutation, deleteFinanceCalendarEventMutation } from 'src/api/app-sync-services';
import { HomePageWizard } from '../HomepageModel';

const scenarioOptions = FINANCE_CALENDAR_SCENARIOS.map((s) => ({ label: s, value: s }));

/** Strip __typename and any other GraphQL metadata from events before sending to mutations */
const cleanEventForMutation = ({ eventId, scenario, dateTime, event, description, source }: FinanceCalendarEventEntity) => ({
  eventId,
  scenario,
  dateTime,
  event,
  description,
  source
});

interface HomePageCalendarStepProps {
  formik: FormikProps<HomePageWizard>;
}

const HomePageCalendarStep: React.FC<HomePageCalendarStepProps> = ({ formik }) => {
  const formikRef = useRef(formik);
  formikRef.current = formik;

  const [events, setEvents] = useState<FinanceCalendarEventEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const [selectedEvents, setSelectedEvents] = useState<FinanceCalendarEventEntity[]>([]);
  const [deletedEventIds, setDeletedEventIds] = useState<string[]>([]);
  const [editingEvent, setEditingEvent] = useState<FinanceCalendarEventEntity | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [newScenario, setNewScenario] = useState<string>(FINANCE_CALENDAR_SCENARIOS[0]);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('10:00:00');
  const [newEventName, setNewEventName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  useEffect(() => {
    fetchFinanceCalendarEvents()
      .then((data) => {
        setEvents(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(`Failed to load calendar events: ${e.message}`);
        setLoading(false);
      });
  }, []);

  // Sync local events state to Formik so the wizard submit has the latest data
  useEffect(() => {
    formikRef.current.setFieldValue('homePageDetailsAndWidgets.financeCalendarEvents', events);
  }, [events]);

  const updateEvents = useCallback((updatedEvents: FinanceCalendarEventEntity[]) => {
    setEvents(updatedEvents);
    setIsDirty(true);
  }, []);

  const resetForm = () => {
    setNewScenario(FINANCE_CALENDAR_SCENARIOS[0]);
    setNewDate('');
    setNewTime('10:00:00');
    setNewEventName('');
    setNewDescription('');
    setEditingEvent(null);
    setIsEditing(false);
  };

  const handleAddOrUpdateEvent = () => {
    if (!newScenario || !newDate || !newEventName) return;
    const dateTime = `${newDate}T${newTime || '10:00:00'}`;
    const event: FinanceCalendarEventEntity = {
      eventId: isEditing && editingEvent ? editingEvent.eventId : uuidv4(),
      scenario: newScenario,
      dateTime,
      event: newEventName,
      description: newDescription,
      source: isEditing && editingEvent ? editingEvent.source || 'manual' : 'manual'
    };
    const updatedEvents = isEditing && editingEvent ? events.map((e) => (e.eventId === editingEvent.eventId ? event : e)) : [...events, event];
    updateEvents(updatedEvents);
    resetForm();
  };

  const handleEditEvent = () => {
    if (selectedEvents.length !== 1) return;
    const event = selectedEvents[0];
    setEditingEvent(event);
    setIsEditing(true);
    const [datePart, timePart] = (event.dateTime || '').split('T');
    setNewScenario(event.scenario);
    setNewDate(datePart || '');
    setNewTime(timePart || '10:00:00');
    setNewEventName(event.event);
    setNewDescription(event.description);
    setSelectedEvents([]);
  };

  const handleDeleteEvents = () => {
    const selectedIds = selectedEvents.map((e) => e.eventId);
    setDeletedEventIds((prev) => [...prev, ...selectedIds]);
    updateEvents(events.filter((e) => !selectedIds.includes(e.eventId)));
    setSelectedEvents([]);
    if (isEditing && editingEvent && selectedIds.includes(editingEvent.eventId)) {
      resetForm();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await Promise.all([
        putFinanceCalendarEventsMutation(events.map(cleanEventForMutation)),
        ...deletedEventIds.map((id) => deleteFinanceCalendarEventMutation(id))
      ]);
      setDeletedEventIds([]);
      setIsDirty(false);
    } catch (e: any) {
      setError(`Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const columnDefinitions: TableProps.ColumnDefinition<FinanceCalendarEventEntity>[] = [
    { id: 'scenario', header: 'Scenario', cell: (item) => item.scenario, sortingField: 'scenario', width: 120 },
    {
      id: 'dateTime',
      header: 'Date/Time',
      sortingField: 'dateTime',
      width: 180,
      cell: (item) => {
        const [d, t] = (item.dateTime || '').split('T');
        return `${d} ${t || ''}`;
      }
    },
    { id: 'event', header: 'Event', cell: (item) => item.event },
    { id: 'description', header: 'Description', cell: (item) => item.description },
    { id: 'source', header: 'Source', cell: (item) => item.source || 'manual', width: 100 }
  ];

  const previewData = React.useMemo(() => toFinanceCalendarWidgetData(events), [events]);

  if (loading) return <Spinner size="large" />;

  return (
    <SpaceBetween size="l" direction="vertical">
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Container
        header={
          <Header variant="h2" description="Add or edit finance calendar events">
            {isEditing ? 'Edit Calendar Event' : 'Add New Calendar Event'}
          </Header>
        }
      >
        <SpaceBetween size="m" direction="vertical">
          <FormField label="Scenario">
            <Select
              selectedOption={scenarioOptions.find((o) => o.value === newScenario) || null}
              onChange={({ detail }) => setNewScenario(detail.selectedOption.value || FINANCE_CALENDAR_SCENARIOS[0])}
              options={scenarioOptions}
              placeholder="Select scenario"
            />
          </FormField>
          <FormField label="Date">
            <DatePicker value={newDate} onChange={({ detail }) => setNewDate(detail.value)} placeholder="YYYY-MM-DD" />
          </FormField>
          <FormField label="Time">
            <TimeInput value={newTime} onChange={({ detail }) => setNewTime(detail.value)} format="hh:mm:ss" placeholder="HH:MM:SS" />
          </FormField>
          <FormField label="Event Name">
            <Input placeholder="e.g., 1st Pass: GP P&L Snapshot" value={newEventName} onChange={({ detail }) => setNewEventName(detail.value)} />
          </FormField>
          <FormField
            label={
              <>
                Description<i> - optional</i>
              </>
            }
          >
            <Input
              placeholder="e.g., 1st Pass: GP P&L snapshot in Prophecy"
              value={newDescription}
              onChange={({ detail }) => setNewDescription(detail.value)}
            />
          </FormField>
          <SpaceBetween size="s" direction="horizontal">
            <Button variant="primary" formAction="none" onClick={handleAddOrUpdateEvent} disabled={!newScenario || !newDate || !newEventName}>
              {isEditing ? 'Update Event' : 'Add Event'}
            </Button>
            {isEditing && (
              <Button variant="link" formAction="none" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      <Table
        columnDefinitions={columnDefinitions}
        items={events}
        selectionType="multi"
        selectedItems={selectedEvents}
        onSelectionChange={({ detail }) => setSelectedEvents(detail.selectedItems)}
        trackBy="eventId"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No Calendar Events</b>
            <Box variant="p" color="inherit">
              Use the form above to add finance calendar events.
            </Box>
          </Box>
        }
        header={
          <Header
            variant="h2"
            counter={`(${events.length})`}
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                <Button formAction="none" onClick={handleEditEvent} disabled={selectedEvents.length !== 1}>
                  Edit
                </Button>
                <Button formAction="none" onClick={handleDeleteEvents} disabled={selectedEvents.length === 0}>
                  Delete
                </Button>
                <Button variant="primary" formAction="none" onClick={handleSave} loading={saving} disabled={!isDirty}>
                  Save Changes
                </Button>
              </SpaceBetween>
            }
          >
            Finance Calendar Events
          </Header>
        }
      />

      {previewData.length > 0 && (
        <Container header={<Header variant="h2">Preview</Header>}>
          <FinanceCalendarWidgetContent hasData="finished" data={previewData} />
        </Container>
      )}
    </SpaceBetween>
  );
};

export default HomePageCalendarStep;
